import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { fetchBookings } from '../../api/lib/bookings';
import { bookingsQueryKeys } from '../../api/query-keys/bookings';
import { BookingsActionsTableMenu } from '../molecules/bookings-actions-table-menu';
import { BookingsActionsSlotPayload } from '../molecules/bookings-table-row';
import { BookingStateFilterPopover } from '../molecules/filters/booking-state-filter-popover';
import { BookingsFilterChips } from '../molecules/filters/bookings-filter-chips';
import { BookingsMoreFilters } from '../organisms/bookings-more-filters';
import { BookingsTable } from '../organisms/bookings-table';
import { CancelBookingDialog } from '../organisms/dialogs/cancel-booking-dialog';
import { ConfirmBookingPresenceDialog } from '../organisms/dialogs/confirm-booking-presence-dialog';
import { ExportBookingsAsXlsDialog } from '../organisms/dialogs/export-bookings-as-xls-dialog/export-bookings-as-xls-dialog';
import { FinishBookingDialog } from '../organisms/dialogs/finish-booking-dialog';

const Bookings = () => {
  const { t } = useTranslation(['bookings', 'common']);

  const navigate = useNavigate();

  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/bookings/_layout/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'from',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    fetchBookings,
    bookingsQueryKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const [
    isOpenConfirmBookingPresenceDialog,
    handleOpenConfirmBookingPresenceDialog,
    handleCloseConfirmBookingPresenceDialog,
  ] = useDialog();

  const [
    isOpenFinishBookingDialog,
    handleOpenFinishBookingDialog,
    handleCloseFinishBookingDialog,
  ] = useDialog();

  const [
    isOpenCancelBookingDialog,
    handleOpenCancelBookingDialog,
    handleCloseCancelBookingDialog,
  ] = useDialog();

  const [
    isOpenExportBookingsAsXlsDialog,
    _handleOpenExportBookingsAsXlsDialog,
    handleCloseExportBookingsAsXlsDialog,
  ] = useDialog();

  const [selectedBooking, setSelectedBooking] =
    useState<BookingsActionsSlotPayload | null>(null);

  const actionsSlot = useCallback(
    ({
      token,
      status,
      confirmedAt,
      spaceToken,
    }: BookingsActionsSlotPayload) => (
      <BookingsActionsTableMenu
        onConfirmPresence={() => {
          setSelectedBooking({ token, status, confirmedAt, spaceToken });
          handleOpenConfirmBookingPresenceDialog();
        }}
        onFinish={() => {
          setSelectedBooking({ token, status, confirmedAt, spaceToken });
          handleOpenFinishBookingDialog();
        }}
        onCancel={() => {
          setSelectedBooking({ token, status, confirmedAt, spaceToken });
          handleOpenCancelBookingDialog();
        }}
        state={status}
        confirmedAt={confirmedAt}
      />
    ),
    [],
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <>
      <Page.Header title={t('bookings:common.title')}>
        {/* TODO: Add download functionality when api is ready */}
        {/* <Can I="download" a="Bookings" passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <Button
                  onClick={handleOpenExportBookingsAsXlsDialog}
                  color="primary"
                  disabled={!allowed}
                  startIcon={<DownloadIcon fontSize="small" />}
                  size="large"
                  variant="outlinedContained"
                  id="download_bookings_data_button">
                  {t('bookings:common.downloadButtonText')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can> */}

        <Can I="create" a="Bookings" passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <Button
                  sx={{ ml: 2 }}
                  onClick={() =>
                    navigate({
                      to: '/tenant/bookings/create',
                      state: { backUrl: location.href },
                    })
                  }
                  color="primary"
                  disabled={!allowed}
                  size="large"
                  variant="contained"
                  id="create_booking_button">
                  {t('bookings:common.createBookingButtonText')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can>
      </Page.Header>

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />

          <BookingStateFilterPopover
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <BookingsMoreFilters
            anchor="right"
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
            resetFilters={resetFilters}
          />
        </Box>

        <BookingsFilterChips
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
        />

        <BookingsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <ConfirmBookingPresenceDialog
        open={isOpenConfirmBookingPresenceDialog}
        onCancel={handleCloseConfirmBookingPresenceDialog}
        bookingToken={selectedBooking?.token ?? ''}
        spaceToken={selectedBooking?.spaceToken ?? ''}
      />

      <FinishBookingDialog
        open={isOpenFinishBookingDialog}
        onCancel={handleCloseFinishBookingDialog}
        bookingToken={selectedBooking?.token ?? ''}
      />

      <CancelBookingDialog
        open={isOpenCancelBookingDialog}
        onCancel={handleCloseCancelBookingDialog}
        bookingToken={selectedBooking?.token ?? ''}
      />

      <ExportBookingsAsXlsDialog
        open={isOpenExportBookingsAsXlsDialog}
        onCancel={handleCloseExportBookingsAsXlsDialog}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Bookings'),
  Bookings,
);
